import { getPB, getSession } from './client';

export type WorkspacePresencePeer = {
  key: string;
  id: string;
  name: string;
  color: string;
  avatarUrl: string | null;
  /** Note path within the workspace the peer currently has open, if any. */
  filePath: string | null;
};

export type WorkspacePresenceLocalState = {
  id: string;
  name: string;
  color: string;
  avatarUrl: string | null;
  filePath: string | null;
};

const COLORS = [
  '#c9a84c',
  '#5a8fc8',
  '#c85050',
  '#5a9a5a',
  '#d4903a',
  '#9b6b9e',
  '#4ecdc4',
];

export function pickPresenceColor(id = ''): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash + id.charCodeAt(i) * 17) % COLORS.length;
  }
  return COLORS[Math.abs(hash) % COLORS.length] ?? COLORS[0] ?? '#5a8fc8';
}

type PresenceListener = (peers: WorkspacePresencePeer[]) => void;

type PresenceRow = {
  user_id: string;
  name: string;
  color: string;
  avatar_url: string | null;
  file_path: string | null;
  updated_at: number;
};

type ActiveWorkspacePresence = {
  workspaceId: string;
  workspacePbId: string;
  local: WorkspacePresenceLocalState;
  listeners: Set<PresenceListener>;
  peers: Map<string, PresenceRow>;
  unsub: (() => void) | null;
  heartbeat: ReturnType<typeof setInterval> | null;
  recordId: string | null;
};

let active: ActiveWorkspacePresence | null = null;

const HEARTBEAT_MS = 20_000;
const STALE_MS = 45_000;

function emitPeers(session: ActiveWorkspacePresence) {
  const now = Date.now();
  const peers: WorkspacePresencePeer[] = [];
  for (const row of session.peers.values()) {
    if (row.user_id === session.local.id) continue;
    if (now - row.updated_at > STALE_MS) continue;
    peers.push({
      key: row.user_id,
      id: row.user_id,
      name: row.name || 'Collaborator',
      color: row.color || pickPresenceColor(row.user_id),
      avatarUrl: row.avatar_url,
      filePath: row.file_path,
    });
  }
  for (const listener of session.listeners) {
    listener(peers);
  }
}

/**
 * Best-effort workspace presence via `workspace_presence` collection realtime.
 * If the collection is missing, presence degrades to local-only (empty peers).
 * TODO: ensure pocketbase setup creates workspace_presence (workspace, user,
 * name, color, avatar_url, file_path) with realtime enabled.
 */
async function upsertPresenceRecord(
  session: ActiveWorkspacePresence,
): Promise<void> {
  const pb = getPB();
  const payload = {
    workspace: session.workspacePbId,
    user: session.local.id,
    name: session.local.name,
    color: session.local.color,
    avatar_url: session.local.avatarUrl,
    file_path: session.local.filePath,
  };
  try {
    if (session.recordId) {
      await pb.collection('workspace_presence').update(session.recordId, payload);
      return;
    }
    try {
      const existing = await pb
        .collection('workspace_presence')
        .getFirstListItem(
          pb.filter('workspace = {:ws} && user = {:uid}', {
            ws: session.workspacePbId,
            uid: session.local.id,
          }),
        );
      session.recordId = existing.id;
      await pb.collection('workspace_presence').update(existing.id, payload);
    } catch {
      const created = await pb.collection('workspace_presence').create(payload);
      session.recordId = created.id;
    }
  } catch (e) {
    // Collection may not exist yet — presence is optional.
    console.warn('workspace presence upsert skipped', e);
  }
}

function ingestRecord(
  session: ActiveWorkspacePresence,
  record: Record<string, unknown>,
) {
  const userId = String(record.user ?? record.user_id ?? '');
  if (!userId) return;
  session.peers.set(userId, {
    user_id: userId,
    name: String(record.name ?? ''),
    color: String(record.color ?? ''),
    avatar_url: (record.avatar_url as string | null) ?? null,
    file_path: (record.file_path as string | null) ?? null,
    updated_at: Date.now(),
  });
}

/**
 * Join (or switch) the sticky workspace presence channel. Callers keep this
 * alive across settings and other non-workspace routes so peers stay visible.
 */
export async function joinWorkspacePresence(params: {
  workspaceId: string;
  local: WorkspacePresenceLocalState;
  onPeers: PresenceListener;
}): Promise<() => void> {
  const { workspaceId, local, onPeers } = params;

  if (active && active.workspaceId === workspaceId) {
    active.local = { ...active.local, ...local };
    active.listeners.add(onPeers);
    await upsertPresenceRecord(active);
    emitPeers(active);
    return () => {
      active?.listeners.delete(onPeers);
    };
  }

  if (active) {
    await leaveWorkspacePresence();
  }

  const sessionCheck = await getSession();
  if (!sessionCheck) {
    return () => {};
  }

  const pb = getPB();
  let workspacePbId = workspaceId;
  try {
    const ws = await pb
      .collection('workspaces')
      .getFirstListItem(
        pb.filter('legacy_id = {:id} || id = {:id}', { id: workspaceId }),
      );
    workspacePbId = ws.id;
  } catch {
    /* use as-is */
  }

  const session: ActiveWorkspacePresence = {
    workspaceId,
    workspacePbId,
    local: { ...local },
    listeners: new Set([onPeers]),
    peers: new Map(),
    unsub: null,
    heartbeat: null,
    recordId: null,
  };
  active = session;

  try {
    const existing = await pb.collection('workspace_presence').getFullList({
      filter: pb.filter('workspace = {:ws}', { ws: workspacePbId }),
    });
    for (const row of existing) {
      ingestRecord(session, row as unknown as Record<string, unknown>);
    }
  } catch {
    /* optional collection */
  }

  try {
    session.unsub = await pb.collection('workspace_presence').subscribe(
      '*',
      (e) => {
        if (active !== session) return;
        if (e.action === 'delete') {
          const uid = String(e.record?.user ?? '');
          if (uid) session.peers.delete(uid);
        } else if (e.record) {
          ingestRecord(
            session,
            e.record as unknown as Record<string, unknown>,
          );
        }
        emitPeers(session);
      },
      { filter: `workspace = "${workspacePbId}"` },
    );
  } catch (e) {
    console.warn('workspace presence subscribe failed', e);
  }

  await upsertPresenceRecord(session);
  emitPeers(session);

  session.heartbeat = setInterval(() => {
    if (active !== session) return;
    void upsertPresenceRecord(session);
    emitPeers(session);
  }, HEARTBEAT_MS);

  return () => {
    session.listeners.delete(onPeers);
  };
}

export async function updateWorkspacePresenceLocal(
  patch: Partial<
    Pick<WorkspacePresenceLocalState, 'name' | 'avatarUrl' | 'filePath'>
  >,
): Promise<void> {
  if (!active) return;
  active.local = { ...active.local, ...patch };
  await upsertPresenceRecord(active);
}

export async function leaveWorkspacePresence(): Promise<void> {
  const session = active;
  if (!session) return;
  active = null;
  session.listeners.clear();
  if (session.heartbeat) clearInterval(session.heartbeat);
  if (session.unsub) {
    try {
      session.unsub();
    } catch {
      // ignore
    }
  }
  if (session.recordId) {
    try {
      await getPB().collection('workspace_presence').delete(session.recordId);
    } catch {
      // ignore
    }
  }
}

export function getActiveWorkspacePresenceId(): string | null {
  return active?.workspaceId ?? null;
}
